import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Repeat, TrendingUp, TrendingDown, Check, Search } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { 
  FREQUENCIES as ALL_FREQUENCIES,
  getFrequencyByValue, 
  getFrequencyLabel 
} from '../constants/frequencies';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

// Catégories par défaut selon le type
const DEFAULT_CATEGORIES = {
  credit: ['Salaire', 'Prime', 'Remboursement', 'Virement reçu', 'Revenus locatifs', 'Freelance', 'Allocation', 'Autre entrée'],
  debit: ['Loyer', 'Courses', 'Transport', 'Électricité', 'Eau', 'Internet', 'Téléphone', 'Assurance', 'Santé', 'Loisirs', 'Restaurant', 'Shopping', 'Abonnements', 'Netflix', 'Spotify', 'Autre dépense']
};

// Fréquences disponibles pour les opérations simples
// Exclut "unique" (ponctuel) car ce formulaire est pour les opérations, pas les récurrences ponctuelles
// Et "quotidien" car il est géré via le formulaire avancé RecurringOperationForm
const OPERATION_FREQUENCIES = ALL_FREQUENCIES.filter(f => 
  !['unique', 'quotidien', 'personnalise'].includes(f.value)
);

const OperationForm = ({ open, onClose, onSuccess, date, calendarId, operation, currency = '€', defaultType, editRecurringType: externalEditRecurringType }) => {
  // États principaux
  const [formType, setFormType] = useState(defaultType || 'debit');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  
  // États pour les catégories
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Récurrence simplifiée
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState('mensuel');
  const [hasEndDate, setHasEndDate] = useState(false);
  const [endDate, setEndDate] = useState('');
  
  // Pour les opérations récurrentes existantes
  const [isEditingRecurring, setIsEditingRecurring] = useState(false);
  const [showRecurringEditDialog, setShowRecurringEditDialog] = useState(false);
  const [recurringEditType, setRecurringEditType] = useState('');
  const [isProjected, setIsProjected] = useState(false);
  
  // Objectifs d'épargne
  const [savingsGoals, setSavingsGoals] = useState([]);
  const [selectedSavingsGoal, setSelectedSavingsGoal] = useState('');

  // Chargement des données au montage
  useEffect(() => {
    if (open && calendarId) {
      loadCategories();
      loadSavingsGoals();
      
      if (operation) {
        // Mode édition
        setFormType(operation.type || 'debit');
        setAmount(operation.amount?.toString() || '');
        setCategory(operation.category || '');
        setIsEditingRecurring(!!operation.recurring_id);
        setIsRecurring(false);
        setIsProjected(operation.is_projected === true);
        
        // If external edit recurring type is provided, use it directly
        if (externalEditRecurringType && operation.recurring_id) {
          setRecurringEditType(externalEditRecurringType);
        }
      } else {
        // Mode création - reset
        setFormType(defaultType || 'debit');
        setAmount('');
        setCategory('');
        setIsRecurring(false);
        setHasEndDate(false);
        setEndDate('');
        setIsEditingRecurring(false);
        setIsProjected(false);
        setCategorySearch('');
        setSelectedSavingsGoal('');
        setRecurringEditType('');
      }
    }
  }, [open, date, operation, calendarId, defaultType, externalEditRecurringType]);

  const loadCategories = async () => {
    try {
      const [userCatsRes, suggestedRes] = await Promise.all([
        axios.get(`${API}/categories?calendar_id=${calendarId}`, { withCredentials: true }),
        axios.get(`${API}/categories/suggested`, { withCredentials: true })
      ]);
      
      const userCats = userCatsRes.data?.map(c => c.name) || [];
      const suggested = suggestedRes.data?.categories || [];
      const allCats = [...new Set([...userCats, ...suggested])];
      setCategories(allCats);
    } catch (error) {
      console.error('Error loading categories:', error);
      // Utiliser les catégories par défaut
      setCategories([...DEFAULT_CATEGORIES.credit, ...DEFAULT_CATEGORIES.debit]);
    }
  };

  const loadSavingsGoals = async () => {
    try {
      const response = await axios.get(`${API}/savings-goals?calendar_id=${calendarId}`, { withCredentials: true });
      setSavingsGoals(response.data || []);
    } catch (error) {
      console.error('Error loading savings goals:', error);
      setSavingsGoals([]);
    }
  };

  // Filtrage des catégories
  const filteredCategories = useMemo(() => {
    const defaultCats = DEFAULT_CATEGORIES[formType] || [];
    const allCats = [...new Set([...categories, ...defaultCats])];
    
    if (!categorySearch.trim()) return allCats.slice(0, 16);
    
    const search = categorySearch.toLowerCase();
    return allCats.filter(cat => cat.toLowerCase().includes(search)).slice(0, 16);
  }, [categories, formType, categorySearch]);

  // Vérifier si la catégorie recherchée existe
  const searchMatchesExisting = useMemo(() => {
    if (!categorySearch.trim()) return true;
    return filteredCategories.some(cat => cat.toLowerCase() === categorySearch.toLowerCase());
  }, [filteredCategories, categorySearch]);

  // Création de catégorie
  const handleCreateCategory = async () => {
    const name = categorySearch.trim();
    if (!name) return;

    try {
      await axios.post(
        `${API}/categories?calendar_id=${calendarId}`,
        { name, type: formType },
        { withCredentials: true }
      );
      setCategories(prev => [...prev, name]);
      setCategory(name);
      setCategorySearch('');
      toast.success(`Catégorie "${name}" créée et sélectionnée`);
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Erreur lors de la création');
    }
  };

  // Sélection de catégorie
  const handleSelectCategory = (cat) => {
    setCategory(cat);
    setCategorySearch('');
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Validations
    if (!category.trim()) {
      toast.error('Veuillez sélectionner une catégorie');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Veuillez entrer un montant valide');
      return;
    }

    setLoading(true);

    try {
      // Extraire le jour du mois de la date sélectionnée
      const selectedDate = new Date(date + 'T00:00:00');
      const dayOfMonth = selectedDate.getDate();

      if (isRecurring && !operation) {
        // CRÉATION D'UNE OPÉRATION RÉCURRENTE avec système harmonisé
        const freqInfo = getFrequencyByValue(frequency);
        await axios.post(
          `${API}/recurring-operations?calendar_id=${calendarId}`,
          {
            name: category,
            type: formType,
            amount: parseFloat(amount),
            category: category,
            day_of_month: dayOfMonth,
            start_date: date,
            end_date: hasEndDate ? endDate : null,
            frequency_type: frequency,
            frequency_value: freqInfo?.unitValue || 1,
            frequency_unit: freqInfo?.unit || 'mois',
            active: true
          },
          { withCredentials: true }
        );
        
        const freqLabel = getFrequencyLabel(frequency, freqInfo?.unit || 'mois', freqInfo?.unitValue || 1);
        toast.success(`Parfait ! Récurrence créée : ${category} - ${freqLabel} 🎯`);
      } else {
        // OPÉRATION SIMPLE OU MODIFICATION
        const data = {
          date: date,
          type: formType,
          amount: parseFloat(amount),
          category: category,
          savings_goal_id: selectedSavingsGoal && selectedSavingsGoal !== 'none' ? selectedSavingsGoal : null
        };

        if (operation) {
          // Modification
          
          // Cas 1: Opération projetée avec "seulement cette occurrence" ou pas de type défini (compatibilité)
          if (isProjected && (!recurringEditType || recurringEditType === 'this_only')) {
            await axios.post(
              `${API}/operations/from-projection?calendar_id=${calendarId}${operation.recurring_id ? `&recurring_id=${operation.recurring_id}` : ''}`,
              data,
              { withCredentials: true }
            );
            toast.success(
              <div className="space-y-1">
                <p className="font-medium">Modification enregistrée</p>
                <p className="text-sm opacity-80">Seule l'occurrence sélectionnée a été modifiée. Les autres restent inchangées.</p>
              </div>,
              { duration: 4000 }
            );
          } else if (isEditingRecurring && !recurringEditType) {
            // Cas 2: Récurrence sans type sélectionné - ouvrir le dialogue
            setShowRecurringEditDialog(true);
            setLoading(false);
            return;
          } else if (isEditingRecurring && recurringEditType) {
            // Cas 3: Récurrence avec type sélectionné - appliquer l'action
            await axios.put(
              `${API}/operations/${operation.id}/edit-recurring`,
              { ...data, edit_type: recurringEditType, operation_date: operation.date },
              { withCredentials: true }
            );
            
            // Messages de confirmation détaillés
            if (recurringEditType === 'this_only') {
              toast.success(
                <div className="space-y-1">
                  <p className="font-medium">Modification enregistrée</p>
                  <p className="text-sm opacity-80">Seule cette occurrence a été modifiée. Les autres restent inchangées.</p>
                </div>,
                { duration: 4000 }
              );
            } else if (recurringEditType === 'future') {
              toast.success(
                <div className="space-y-1">
                  <p className="font-medium">Modification enregistrée</p>
                  <p className="text-sm opacity-80">Cette occurrence et toutes les suivantes ont été mises à jour. Les occurrences passées restent inchangées.</p>
                </div>,
                { duration: 4000 }
              );
            } else {
              toast.success(
                <div className="space-y-1">
                  <p className="font-medium">Modification enregistrée</p>
                  <p className="text-sm opacity-80">Toutes les occurrences de cette série ont été mises à jour.</p>
                </div>,
                { duration: 4000 }
              );
            }
          } else {
            await axios.put(`${API}/operations/${operation.id}`, data, { withCredentials: true });
            toast.success('Modification enregistrée 👌');
          }
        } else {
          // Création simple
          await axios.post(`${API}/operations?calendar_id=${calendarId}`, data, { withCredentials: true });
          toast.success('Parfait, c\'est enregistré ! ✨');
        }
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving operation:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const selectedDayOfMonth = date ? new Date(date + 'T00:00:00').getDate() : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="operation-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {operation ? 'Modifier l\'opération' : 'Nouvelle opération ✨'}
            </DialogTitle>
            <DialogDescription>
              {date && new Date(date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* TYPE : ENTRÉE / DÉPENSE */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setFormType('credit'); setCategory(''); }}
                className={`p-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${
                  formType === 'credit' 
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                    : 'border-gray-200 hover:border-emerald-200'
                }`}
              >
                <TrendingUp className="h-4 w-4" />
                <span className="font-medium text-sm">Entrée</span>
              </button>
              <button
                type="button"
                onClick={() => { setFormType('debit'); setCategory(''); }}
                className={`p-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${
                  formType === 'debit' 
                    ? 'border-rose-400 bg-rose-50 text-rose-600' 
                    : 'border-gray-200 hover:border-rose-200'
                }`}
              >
                <TrendingDown className="h-4 w-4" />
                <span className="font-medium text-sm">Dépense</span>
              </button>
            </div>

            {/* MONTANT */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">C'était combien ? 💸</Label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Ex : 42.50"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  className="text-2xl font-semibold text-center h-14 pr-10 rounded-xl"
                  data-testid="amount-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">{currency}</span>
              </div>
            </div>

            {/* CATÉGORIE */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Dans quel tiroir on range ça ? {category && <Check className="inline h-3 w-3 text-teal-600" />}
              </Label>
              
              {/* Recherche */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Courses, resto, sortie..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  className="pl-9 h-10 rounded-xl"
                  data-testid="category-search"
                />
              </div>
              
              {/* Bouton créer si nouvelle catégorie */}
              {categorySearch.trim() && !searchMatchesExisting && (
                <Button
                  type="button"
                  onClick={handleCreateCategory}
                  variant="outline"
                  className="w-full h-9 text-teal-600 border-teal-300 hover:bg-teal-50 rounded-xl"
                  data-testid="create-category-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Créer "{categorySearch.trim()}"
                </Button>
              )}
              
              {/* Liste des catégories */}
              <div className="max-h-28 overflow-y-auto rounded-xl border p-2 bg-gray-50">
                {filteredCategories.length === 0 ? (
                  <div className="text-center py-2 text-xs text-gray-500">
                    Aucune catégorie trouvée
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {filteredCategories.map((cat, idx) => (
                      <button
                        key={`cat-${idx}`}
                        type="button"
                        onClick={() => handleSelectCategory(cat)}
                        className={`px-2.5 py-1 text-xs rounded-full transition-all font-medium ${
                          category === cat
                            ? 'bg-teal-600 text-white'
                            : 'bg-white hover:bg-teal-50 border border-gray-200'
                        }`}
                      >
                        {category === cat && <Check className="inline h-3 w-3 mr-1" />}
                        {cat}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* RÉCURRENCE (SIMPLIFIÉ) */}
            {!operation && (
              <div className={`p-3 rounded-xl border transition-all ${isRecurring ? 'border-teal-500 bg-teal-50' : 'border-gray-200'}`}>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="recurring"
                    checked={isRecurring}
                    onCheckedChange={setIsRecurring}
                    data-testid="recurring-checkbox"
                  />
                  <Label htmlFor="recurring" className="flex items-center gap-2 cursor-pointer text-sm">
                    <Repeat className="h-4 w-4 text-teal-600" />
                    <span className="font-medium">Opération récurrente</span>
                  </Label>
                </div>
                
                {isRecurring && (
                  <div className="mt-3 pt-3 border-t border-teal-200 space-y-3">
                    {/* Sélecteur de fréquence - système harmonisé */}
                    <div className="space-y-2">
                      <Label className="text-xs text-teal-700">Fréquence</Label>
                      <Select value={frequency} onValueChange={setFrequency}>
                        <SelectTrigger className="h-10 rounded-lg bg-white" data-testid="frequency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OPERATION_FREQUENCIES.map(f => (
                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Info sur la récurrence */}
                    <div className="bg-teal-100 rounded-lg p-3">
                      <p className="text-sm text-teal-800">
                        {frequency === 'hebdomadaire' 
                          ? `Cette opération se répètera chaque semaine`
                          : frequency === 'quinzaine'
                            ? `Cette opération se répètera toutes les 2 semaines`
                          : frequency === 'annuel'
                            ? `Cette opération se répètera chaque année le ${selectedDayOfMonth}/${new Date(date + 'T00:00:00').getMonth() + 1}`
                            : `Cette opération se répètera le ${selectedDayOfMonth} (${getFrequencyLabel(frequency, 'mois', 1)})`
                        }
                      </p>
                    </div>
                    
                    {/* Date de fin optionnelle */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="has-end-date"
                          checked={hasEndDate}
                          onCheckedChange={setHasEndDate}
                        />
                        <Label htmlFor="has-end-date" className="text-xs cursor-pointer">Définir une date de fin</Label>
                      </div>
                      {hasEndDate && (
                        <Input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="h-9 rounded-lg"
                          min={date}
                          data-testid="end-date-input"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* INFO RÉCURRENCE EXISTANTE */}
            {operation && isEditingRecurring && (
              <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-center gap-2 text-amber-800">
                  <Repeat className="h-4 w-4" />
                  <span className="text-sm font-medium">Opération récurrente</span>
                </div>
                <p className="text-xs text-amber-600 mt-1">
                  La modification vous demandera si vous souhaitez modifier cette occurrence ou toute la série.
                </p>
              </div>
            )}

            {/* OBJECTIF D'ÉPARGNE */}
            {formType === 'debit' && savingsGoals.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-gray-500">
                  Verser dans un objectif (optionnel)
                </Label>
                <Select value={selectedSavingsGoal} onValueChange={setSelectedSavingsGoal}>
                  <SelectTrigger className="h-10 rounded-xl" data-testid="savings-goal-select">
                    <SelectValue placeholder="Aucun objectif" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun objectif</SelectItem>
                    {savingsGoals.map(goal => (
                      <SelectItem key={goal.id} value={goal.id}>{goal.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* BOUTONS D'ACTION */}
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={onClose} className="flex-1 h-11 rounded-xl">
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={loading || !category || !amount} 
                className="flex-1 h-11 rounded-xl bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="save-operation-btn"
              >
                {loading ? 'Un instant...' : operation ? 'Modifier' : isRecurring ? 'Créer la récurrence' : 'Hop, c\'est noté ! ✨'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* DIALOGUE MODIFICATION RÉCURRENTE */}
      <Dialog open={showRecurringEditDialog} onOpenChange={setShowRecurringEditDialog}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle>Modifier l'opération récurrente</DialogTitle>
            <DialogDescription>
              Quelle(s) occurrence(s) souhaitez-vous modifier ?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Button
              onClick={() => {
                setRecurringEditType('this_only');
                setShowRecurringEditDialog(false);
                setTimeout(() => handleSubmit(), 100);
              }}
              variant="outline"
              className="w-full justify-start text-left h-auto py-3 rounded-xl"
            >
              <div>
                <div className="font-medium text-sm">Cette occurrence uniquement</div>
                <div className="text-xs text-gray-500">Modifier seulement cette date</div>
              </div>
            </Button>
            <Button
              onClick={() => {
                setRecurringEditType('future');
                setShowRecurringEditDialog(false);
                setTimeout(() => handleSubmit(), 100);
              }}
              variant="outline"
              className="w-full justify-start text-left h-auto py-3 rounded-xl"
            >
              <div>
                <div className="font-medium text-sm">Cette occurrence et les suivantes</div>
                <div className="text-xs text-gray-500">Modifier à partir de cette date</div>
              </div>
            </Button>
            <Button
              onClick={() => {
                setRecurringEditType('all');
                setShowRecurringEditDialog(false);
                setTimeout(() => handleSubmit(), 100);
              }}
              variant="outline"
              className="w-full justify-start text-left h-auto py-3 rounded-xl"
            >
              <div>
                <div className="font-medium text-sm">Toutes les occurrences</div>
                <div className="text-xs text-gray-500">Modifier toute la série</div>
              </div>
            </Button>
          </div>
          <Button variant="ghost" onClick={() => { setShowRecurringEditDialog(false); setLoading(false); }}>
            Annuler
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OperationForm;
